import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getBillingInfo, openBillingPortal, cancelSubscription, type BillingInfo } from '../../../api/client';

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
    trialing: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
    past_due: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/25',
    canceled: 'bg-red-500/15 text-red-300 border-red-500/25',
    paused:   'bg-stone-500/15 text-stone-300 border-stone-500/25',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${map[status] ?? map.paused}`}>
      {status}
    </span>
  );
}

export default function BillingPage() {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo]           = useState<BillingInfo | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [working, setWorking]     = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const justUpgraded = urlParams.get('success') === '1';
  const upgradedPlan = urlParams.get('plan') ?? '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBillingInfo();
      setInfo(data);
    } catch {
      setError('Could not load billing info. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    load();
  }, [isLoggedIn, navigate, load]);

  const handlePortal = async () => {
    setWorking(true);
    setError('');
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open billing portal.');
    } finally {
      setWorking(false);
    }
  };

  const handleCancel = async (resume = false) => {
    if (!resume && !window.confirm('Cancel your Pro subscription at the end of the billing period?')) return;
    setWorking(true);
    setError('');
    try {
      const { message } = await cancelSubscription(resume);
      setActionMsg(message);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subscription.');
    } finally {
      setWorking(false);
    }
  };

  const isPro     = info?.plan_slug === 'pro';
  const sub       = info?.subscription;
  const isStripe  = sub?.provider === 'stripe';

  const formatDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#1c1917_0%,#3f2a15_60%,#1c1917_100%)] pt-14 text-white">
      {/* Header */}
      <div className="px-4 pt-8 pb-2">
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="inline-flex items-center gap-2 text-stone-400 hover:text-amber-300 text-sm transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
          Back to profile
        </button>
      </div>

      <div className="mx-auto max-w-2xl px-4 pb-24 pt-6">
        <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        <p className="mt-1 text-stone-400 text-sm">Manage your Frequency Locus subscription and payment history.</p>

        {justUpgraded && (
          <div className="mt-5 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 px-5 py-4">
            <p className="text-emerald-300 font-semibold text-sm">
              🎉 Welcome to {upgradedPlan === 'pro' ? 'Pro' : upgradedPlan}! Your upgrade is being processed — it may take a moment to reflect.
            </p>
          </div>
        )}

        {error && (
          <div className="mt-5 rounded-2xl bg-red-500/10 border border-red-500/20 px-5 py-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {actionMsg && (
          <div className="mt-5 rounded-2xl bg-amber-500/10 border border-amber-500/20 px-5 py-4">
            <p className="text-amber-300 text-sm">{actionMsg}</p>
          </div>
        )}

        {loading ? (
          <div className="mt-8 space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 rounded-2xl border border-stone-700 bg-stone-900/50 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {/* Current plan */}
            <div className="rounded-2xl border border-stone-700/60 bg-stone-900/40 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Current Plan</p>
                  <p className="text-xl font-bold capitalize">{info?.plan_slug ?? 'Free'}</p>
                  {sub && <StatusBadge status={sub.status} />}
                </div>
                {!isPro && (
                  <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    className="shrink-0 bg-gradient-to-r from-amber-400 to-orange-400 text-stone-950 font-semibold px-4 py-2 rounded-xl text-sm hover:opacity-90 transition-opacity"
                  >
                    Upgrade to Pro
                  </button>
                )}
              </div>

              {sub && (
                <div className="mt-4 pt-4 border-t border-stone-700/50 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-stone-500 text-xs">Provider</p>
                    <p className="text-stone-200 capitalize mt-0.5">{sub.provider}</p>
                  </div>
                  <div>
                    <p className="text-stone-500 text-xs">Period ends</p>
                    <p className="text-stone-200 mt-0.5">{formatDate(sub.current_period_end)}</p>
                  </div>
                  {sub.cancel_at_period_end && (
                    <div className="col-span-2">
                      <p className="text-amber-300 text-xs">
                        ⚠ Cancels on {formatDate(sub.current_period_end)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Stripe actions */}
            {isPro && isStripe && (
              <div className="rounded-2xl border border-stone-700/60 bg-stone-900/40 p-6 space-y-3">
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-3">Manage Subscription</p>
                <button
                  type="button"
                  disabled={working}
                  onClick={handlePortal}
                  className="w-full border border-stone-600 text-white font-medium py-2.5 rounded-xl text-sm hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  Open Stripe Billing Portal
                </button>
                {sub?.cancel_at_period_end ? (
                  <button
                    type="button"
                    disabled={working}
                    onClick={() => handleCancel(true)}
                    className="w-full border border-emerald-600/50 text-emerald-300 font-medium py-2.5 rounded-xl text-sm hover:bg-emerald-500/5 transition-colors disabled:opacity-50"
                  >
                    Resume subscription
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={working}
                    onClick={() => handleCancel(false)}
                    className="w-full border border-red-600/40 text-red-400 font-medium py-2.5 rounded-xl text-sm hover:bg-red-500/5 transition-colors disabled:opacity-50"
                  >
                    Cancel at period end
                  </button>
                )}
              </div>
            )}

            {/* Paystack notice */}
            {isPro && !isStripe && (
              <div className="rounded-2xl border border-stone-700/60 bg-stone-900/40 p-6">
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Paystack Subscription</p>
                <p className="text-stone-400 text-sm">
                  Your subscription is managed via Paystack. To cancel or update your plan, use the link in your Paystack email or contact support.
                </p>
              </div>
            )}

            {/* Payment history */}
            {info && info.recent_payments.length > 0 && (
              <div className="rounded-2xl border border-stone-700/60 bg-stone-900/40 p-6">
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-4">Recent Payments</p>
                <div className="space-y-3">
                  {info.recent_payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-stone-300 capitalize">{p.provider}</p>
                        <p className="text-stone-500 text-xs mt-0.5">{formatDate(p.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-stone-200">
                          {p.currency === 'NGN' ? '₦' : '$'}{Number(p.amount).toLocaleString()}
                        </p>
                        <p className={`text-xs mt-0.5 ${p.status === 'succeeded' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {p.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/account/billing/history')}
                  className="mt-4 text-amber-400 hover:text-amber-300 text-xs transition-colors"
                >
                  View full history →
                </button>
              </div>
            )}

            {/* No activity */}
            {info && !sub && !isPro && (
              <div className="rounded-2xl border border-dashed border-stone-700/50 p-8 text-center">
                <p className="text-stone-500 text-sm">No active subscription.</p>
                <button
                  type="button"
                  onClick={() => navigate('/pricing')}
                  className="mt-4 bg-gradient-to-r from-amber-400 to-orange-400 text-stone-950 font-semibold px-6 py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity"
                >
                  See plans
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
